export type CustomerDashboardSection =
  | 'navigation'
  | 'habitTracking'
  | 'supportGroups'
  | 'coachingSessions'
  | 'miniCourses'
  | 'changeTrips'
  | 'treatmentAid'
  | 'pointsRewards'
  | 'organizationCompetitions'
  | 'settings'
  | 'intermittentFasting'
  | 'mealLogs'
  | 'activityLogs'
  | 'sleepLogs'
  | 'healthMeasures';

export type CustomerPermissionAction = 
  | 'createSupportGroup';

export interface CustomerSectionPermissions {
  navigation: boolean;
  habitTracking: boolean;
  supportGroups: boolean;
  coachingSessions: boolean;
  miniCourses: boolean;
  changeTrips: boolean;
  treatmentAid: boolean;
  pointsRewards: boolean;
  organizationCompetitions: boolean;
  settings: boolean;
  intermittentFasting: boolean;
  mealLogs: boolean;
  activityLogs: boolean;
  sleepLogs: boolean;
  healthMeasures: boolean;
}

export interface CustomerActionPermissions {
  createSupportGroup: boolean;
}

export interface CustomerPermissions {
  sections: CustomerSectionPermissions;
  actions: CustomerActionPermissions;
}

export interface UserPermissionOverride {
  userId: string;
  permissions: CustomerPermissions;
  overriddenAt: Date;
  overriddenBy: string; // Admin user ID
}

export interface OrganizationPermissionOverride {
  organizationId: string;
  organizationName: string;
  permissions: CustomerPermissions;
  overriddenAt: Date;
  overriddenBy: string; // Admin user ID
}

export interface GlobalCustomerPermissions {
  id: string;
  defaultPermissions: CustomerPermissions;
  userOverrides: UserPermissionOverride[];
  organizationOverrides: OrganizationPermissionOverride[];
  updatedAt: Date;
  updatedBy: string; // Admin user ID
}

// Helper type for the permission modal
export interface PermissionTarget {
  type: 'global' | 'user' | 'organization';
  userId?: string; // Only for user-specific permissions
  userName?: string; // Only for user-specific permissions
  organizationId?: string; // Only for organization-specific permissions
  organizationName?: string; // Only for organization-specific permissions
}

// Default permissions (all enabled by default)
export const DEFAULT_CUSTOMER_PERMISSIONS: CustomerPermissions = {
  sections: {
    navigation: true,
    habitTracking: true,
    supportGroups: true,
    coachingSessions: true,
    miniCourses: true,
    changeTrips: true,
    treatmentAid: true,
    pointsRewards: true,
    organizationCompetitions: true,
    settings: true,
    intermittentFasting: true,
    mealLogs: true,
    activityLogs: true,
    sleepLogs: true,
    healthMeasures: true,
  },
  actions: {
    createSupportGroup: true,
  },
};

// Section labels for UI
export const CUSTOMER_SECTION_LABELS: Record<CustomerDashboardSection, { en: string; ar: string }> = {
  navigation: { en: 'Navigation Bar', ar: 'شريط التنقل' },
  habitTracking: { en: 'Habit Tracking', ar: 'تتبع العادات' },
  supportGroups: { en: 'Support Groups', ar: 'مجموعات الدعم' },
  coachingSessions: { en: 'Coaching Sessions', ar: 'جلسات التدريب' },
  miniCourses: { en: 'Mini Courses', ar: 'الدورات المصغرة' },
  changeTrips: { en: 'Change Trips', ar: 'رحلات التغيير' },
  treatmentAid: { en: 'Treatment Aid', ar: 'طلبات العلاج' },
  pointsRewards: { en: 'Points & Rewards', ar: 'النقاط والمكافآت' },
  organizationCompetitions: { en: 'Organization Competitions', ar: 'منافسات المنظمة' },
  settings: { en: 'Settings', ar: 'الإعدادات' },
  intermittentFasting: { en: 'Intermittent Fasting', ar: 'الصيام المتقطع' },
  mealLogs: { en: 'Meal Logs', ar: 'تسجيل الوجبات' },
  activityLogs: { en: 'Activity Logs', ar: 'تسجيل النشاط' },
  sleepLogs: { en: 'Sleep Logs', ar: 'تسجيل النوم' },
  healthMeasures: { en: 'Health Measures', ar: 'القياسات الصحية' },
};

// Action labels for UI
export const CUSTOMER_ACTION_LABELS: Record<CustomerPermissionAction, { en: string; ar: string }> = {
  createSupportGroup: { en: 'Create Support Group', ar: 'إنشاء مجموعة دعم' },
};